import './SettingsShell.css'

/**
 * Shared sidebar-plus-panel layout for the campaign settings and site
 * settings pages. Replaces the old "single narrow stack of cards" pattern
 * (each page had its own bespoke, centred, max-width: 600-720px column)
 * with a wide two-pane layout that matches the rest of the app's card
 * idiom instead of drifting off on its own.
 *
 * This component only renders the shell (header + rail + panel). It does
 * not know anything about what a "section" contains — the caller renders
 * the active section's fields as `children` and swaps them when `onSelect`
 * fires with a new section id.
 *
 * @param {string} title - Page title, e.g. "Campaign Settings".
 * @param {string} [subtitle] - Small line under the title, e.g. the campaign name.
 * @param {string} [icon] - Emoji/icon shown in the header badge.
 * @param {{id: string, label: string, icon?: string}[]} sections - Left rail entries.
 * @param {string} activeId - id of the currently selected section.
 * @param {(id: string) => void} onSelect - Called when a rail entry is clicked.
 */
function SettingsShell({ title, subtitle, icon, sections = [], activeId, onSelect, children }) {
  return (
    <div className="ss-page">
      <div className="ss-container">
        <header className="ss-header">
          {icon && (
            <span className="ss-header-icon" aria-hidden="true">
              {icon}
            </span>
          )}
          <div className="ss-header-text">
            <h1>{title}</h1>
            {subtitle && <p className="ss-subtitle">{subtitle}</p>}
          </div>
        </header>

        <div className="ss-body">
          <nav className="ss-rail" aria-label="Settings sections">
            {sections.map((section) => {
              const isActive = section.id === activeId
              return (
                <button
                  key={section.id}
                  type="button"
                  className={`ss-rail-item${isActive ? ' is-active' : ''}`}
                  onClick={() => onSelect?.(section.id)}
                  aria-current={isActive ? 'true' : undefined}
                >
                  {section.icon && (
                    <span className="ss-rail-icon" aria-hidden="true">
                      {section.icon}
                    </span>
                  )}
                  <span className="ss-rail-label">{section.label}</span>
                </button>
              )
            })}
          </nav>

          <div className="ss-panel">{children}</div>
        </div>
      </div>
    </div>
  )
}

export default SettingsShell
